import { AdPositionElements, insertBanners } from '@schibsted-nmp/advertising-companion';
import type { ReactNode } from 'react';
import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import type { Filters, ResultDataStats, ResultItem as ResultItemType } from '@/domain';
import { PaginationWrapper } from '../pagination/PaginationWrapper';
import { ResultItem } from '../resultItem/ResultItem';
import { SkeletonResultList } from '../skeletonResultList/SkeletonResultList';

export interface ResultListProps {
    stats: ResultDataStats;
    filters: Filters;
    onFilterChanged: (filter: Filters) => void;
    numberOfPreloadedImages: number;
    results: ResultItemType[] | false | null;
    loginId: number;
    loading?: boolean;
}

export const ResultList = ({
    stats,
    filters,
    onFilterChanged,
    numberOfPreloadedImages,
    results,
    loading = false,
    loginId,
}: ResultListProps) => {
    const [bannerPositions, bannerElements] = AdPositionElements.use({ layoutType: 'grid' });

    const renderItemNodes = () =>
        (results as ResultItemType[]).map((item, index) => (
            <ResultItem
                key={`${item.id}${item.upsell ? '-fus' : ''}`}
                rank={index}
                item={item}
                filters={filters}
                imagePreloaded={index < numberOfPreloadedImages}
                loginId={loginId}
            />
        ));

    const renderItemsAndPagination = () => {
        const items = renderItemNodes();
        const adItemsWithBanners = insertBanners({
            adItems: items,
            bannerPositions: bannerPositions as number[],
            bannerElements: bannerElements as ReactNode[],
        });

        return (
            <>
                <section
                    aria-labelledby="ads-heading"
                    id="ads"
                    className="grid grid-flow-row-dense md:grid-cols-2 gap-16"
                    data-testid="resultList">
                    <h1 className="sr-only" id="ads-heading">
                        Søkeresultater
                    </h1>
                    {adItemsWithBanners}
                </section>
                <PaginationWrapper stats={stats} onFilterChanged={onFilterChanged} />
            </>
        );
    };

    const renderFetchFailedMessage = () => (
        <p className="p-16 text-22 font-bold text-center" data-testid="fetchFailed">
            {/* biome-ignore lint/a11y/useValidAnchor: This existed before biome was added */}
            Noe gikk galt under henting av tilbud. <a href="">Prøv å laste siden på nytt</a>.
        </p>
    );

    const renderNoResultsMessage = () => (
        <p className="p-16 text-22 font-bold text-center" data-testid="emptyResultList">
            {loading ? (
                <SpinnerIcon data-testid="newSearchSpinner" size={24} className="inline" />
            ) : (
                'Ingen treff akkurat nå. Zoom ut i kartet for å få flere treff.'
            )}
        </p>
    );

    if (results === null) {
        return <SkeletonResultList />;
    }

    if (!results) {
        return renderFetchFailedMessage();
    }

    if (results.length === 0) {
        return renderNoResultsMessage();
    }

    return renderItemsAndPagination();
};
